import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Skeleton } from '@shared/components/Skeleton';

type BarProps = Readonly<{
  height: number;
  radius?: number;
  width: number | string;
}>;

const Bar: React.FC<BarProps> = ({ width, height, radius = 6 }) => (
  <Skeleton
    flexShrink={0}
    height={pxToRem(height)}
    borderRadius={pxToRem(radius)}
    width={typeof width === 'number' ? pxToRem(width) : width}
  />
);

// Mirrors the summary strip's own cell: divided down the columns on wide screens and across the rows
// when they stack.
const SummaryCell: React.FC<{ children: React.ReactNode; isLast?: boolean }> = ({ children, isLast }) => (
  <Flex
    gap={3}
    flex={1}
    padding={6}
    minWidth={0}
    borderColor="mono3"
    flexDirection="column"
    justifyContent="center"
    borderRightStyle="solid"
    borderBottomStyle="solid"
    borderRightWidth={isLast ? 0 : { _: 0, md: '1px' }}
    borderBottomWidth={isLast ? 0 : { _: '1px', md: 0 }}
  >
    {children}
  </Flex>
);

const StayRowSkeleton: React.FC<{ isLast?: boolean }> = ({ isLast }) => (
  <Flex
    gap={4}
    paddingY={4}
    borderColor="mono2"
    alignItems="center"
    paddingX={{ _: 4, md: 5 }}
    borderBottom={isLast ? undefined : '1px solid'}
  >
    <Flex
      gap={3}
      flex={2}
      minWidth={0}
      alignItems="center"
    >
      <Skeleton
        flexShrink={0}
        borderRadius="50%"
        width={pxToRem(32)}
        height={pxToRem(32)}
      />
      <Bar
        width="60%"
        height={13}
      />
    </Flex>
    <Box flex={1.5}>
      <Bar
        width="70%"
        height={13}
      />
    </Box>
    <Box flex={1.2}>
      <Bar
        width="50%"
        height={13}
      />
    </Box>
    <Box flex={1.2}>
      <Bar
        width="65%"
        height={13}
      />
    </Box>
  </Flex>
);

// One lodging card: photo flush in the card's leading corner, name + address, counts on the right,
// then the expanded stay table — the same anatomy the loaded card renders.
const LodgingCardSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <Box
    backgroundColor="white"
    border="1px solid"
    borderColor="mono3"
    borderRadius={pxToRem(12)}
    overflow="hidden"
  >
    <Flex
      alignItems="stretch"
      minHeight={pxToRem(96)}
    >
      <Skeleton
        width={pxToRem(96)}
        minWidth={pxToRem(96)}
        alignSelf="stretch"
        borderRadius={0}
      />
      <Flex
        flex={1}
        alignItems="center"
        gap={4}
        minWidth={0}
        paddingX={{ _: 4, md: 5 }}
        paddingY={3}
      >
        <Flex
          flex={1}
          flexDirection="column"
          gap={2}
          minWidth={0}
        >
          <Bar
            width="55%"
            height={17}
          />
          <Bar
            width="40%"
            height={13}
          />
        </Flex>
        <Flex
          flexShrink={0}
          flexDirection="column"
          alignItems="flex-end"
          gap={2}
        >
          <Bar
            width={64}
            height={13}
          />
          <Bar
            width={48}
            height={11}
          />
        </Flex>
      </Flex>
    </Flex>

    <Box
      borderTop="1px solid"
      borderColor="mono3"
    >
      {/* Column header strip, tinted like the real one rather than drawn as bars. */}
      <Box
        height={pxToRem(40)}
        backgroundColor="mono1"
        borderBottom="1px solid"
        borderColor="mono3"
      />
      {Array.from({ length: rowCount }, (_, index) => (
        <StayRowSkeleton
          key={index}
          isLast={index === rowCount - 1}
        />
      ))}
    </Box>
  </Box>
);

/**
 * Stand-in for the dashboard while its two queries settle, and for the window where the feature flag
 * itself is still resolving.
 *
 * Deliberately the same skeleton as the page it replaces — header, summary strip, toolbar, status
 * tabs, count line, lodging cards — down to the borders and card radii, so the layout is already in
 * place when the data lands and nothing shifts under the reader. It carries the dashboard's own
 * horizontal inset for the same reason: both callers render it in place of the padded page, so the
 * padding has to live here rather than at either call site.
 */
export const GuestStaysDashboardSkeleton: React.FC = () => (
  <Flex
    flexDirection="column"
    gap={6}
    width="100%"
    paddingX={{ _: 6, sm2: 8 }}
    paddingBottom={8}
  >
    {/* Header: title + subtitle, request CTA opposite. */}
    <Flex
      gap={4}
      justifyContent="space-between"
      flexDirection={{ _: 'column', sm2: 'row' }}
      alignItems={{ _: 'stretch', sm2: 'flex-start' }}
    >
      <Flex
        gap={2}
        flexDirection="column"
      >
        <Bar
          width={180}
          height={24}
        />
        <Bar
          width={340}
          height={15}
        />
      </Flex>
      <Bar
        width={210}
        height={44}
        radius={9999}
      />
    </Flex>

    {/* Summary strip. */}
    <Flex
      flexDirection={{ _: 'column', md: 'row' }}
      border="1px solid"
      borderColor="mono3"
      borderRadius={pxToRem(16)}
      backgroundColor="white"
      overflow="hidden"
    >
      <SummaryCell>
        <Bar
          width={120}
          height={22}
        />
        <Bar
          width={96}
          height={13}
        />
        <Bar
          width={240}
          height={6}
          radius={9999}
        />
      </SummaryCell>
      <SummaryCell>
        <Bar
          width={140}
          height={22}
        />
        <Bar
          width={72}
          height={13}
        />
      </SummaryCell>
      <SummaryCell isLast>
        <Flex
          alignItems="center"
          gap={4}
        >
          <Skeleton
            width={pxToRem(40)}
            height={pxToRem(40)}
            borderRadius="50%"
            flexShrink={0}
          />
          <Flex
            flexDirection="column"
            gap={2}
          >
            <Bar
              width={190}
              height={15}
            />
            <Bar
              width={120}
              height={13}
            />
          </Flex>
        </Flex>
      </SummaryCell>
    </Flex>

    {/* Toolbar: view toggle opposite search + filter. */}
    <Flex
      alignItems={{ _: 'stretch', sm2: 'center' }}
      justifyContent="space-between"
      flexDirection={{ _: 'column', sm2: 'row' }}
      gap={3}
    >
      <Bar
        width={230}
        height={44}
        radius={12}
      />
      <Flex
        alignItems="center"
        gap={3}
        flex={{ _: undefined, sm2: 1 }}
        justifyContent="flex-end"
        minWidth={0}
      >
        <Box
          flex={1}
          minWidth={0}
          maxWidth={{ _: undefined, sm2: pxToRem(420) }}
        >
          <Bar
            width="100%"
            height={44}
            radius={9999}
          />
        </Box>
        <Bar
          width={92}
          height={44}
          radius={9999}
        />
      </Flex>
    </Flex>

    <Flex
      paddingBottom={4}
      borderColor="mono3"
      alignItems="center"
      gap={{ _: 5, sm2: 7 }}
      borderBottom="1px solid"
    >
      <Bar
        width={86}
        height={15}
      />
      <Bar
        width={118}
        height={15}
      />
      <Bar
        width={104}
        height={15}
      />
      <Bar
        width={120}
        height={15}
      />
    </Flex>

    <Flex
      gap={4}
      alignItems="center"
      justifyContent="space-between"
    >
      <Bar
        width={130}
        height={13}
      />
      <Bar
        width={150}
        height={32}
        radius={9999}
      />
    </Flex>

    <Flex
      flexDirection="column"
      gap={4}
    >
      <LodgingCardSkeleton rowCount={2} />
      <LodgingCardSkeleton rowCount={1} />
    </Flex>
  </Flex>
);
